import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from '../../../utils/firebase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ForgotPasswordForm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const validateEmail = (email) => {
    // Enhanced email validation for web standards
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check internet connectivity
    if (!isOnline) {
      setMessage({
        text: 'Please check your internet connection and try again.',
        type: 'error'
      });
      return;
    }
    
    // Validate email
    if (!validateEmail(email)) {
      setMessage({
        text: 'Please enter a valid email address',
        type: 'error'
      });
      return;
    }

    setIsLoading(true);
    setMessage({ text: '', type: '' });

    try {
      // Web configuration for password reset
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: true,
      };
    
      await sendPasswordResetEmail(auth, email, {
        // Optional configuration
        url: window.location.origin + "/login", // Redirect URL after password reset
        handleCodeInApp: true,
      });

      setMessage({
        text: 'Password reset link has been sent to your email. Please check your inbox and spam folder.',
        type: 'success'
      });
      setEmail('');
    } catch (error) {
      let errorMessage = 'Failed to send reset link. Please try again.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account exists with this email address.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many attempts. Please try again later.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection and try again.';
          break;
        default:
          errorMessage = 'An error occurred. Please try again later.';
      }

      setMessage({
        text: errorMessage,
        type: 'error'
      });
      
      // Log error for monitoring
      console.error('Password reset error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card border border-border rounded-lg shadow-card p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="KeyRound" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">Reset Password</h1>
          <p className="text-muted-foreground">Enter your email address to receive a password reset link</p>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div 
            className={`mb-6 p-4 ${message.type === 'success' ? 'bg-success/10 border-success/20 text-success' : 'bg-destructive/10 border-destructive/20 text-destructive'} border rounded-lg flex items-center space-x-3`}
            role="alert"
            aria-live="polite"
          >
            <Icon 
              name={message.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
              size={20} 
              className={message.type === 'success' ? 'text-success' : 'text-destructive'} 
            />
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        {/* Reset Password Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e?.target?.value)}
            required
            disabled={isLoading}
            autoComplete="email"
            spellCheck="false"
            autoCapitalize="none"
          />

          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isLoading}
            disabled={isLoading || !isOnline}
            className="h-12"
          >
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>

          <div className="text-center mt-4">
            <Button
              variant="link"
              onClick={() => navigate('/login')}
              className="text-sm p-0 h-auto"
            >
              Back to Login
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;